type IndexKey = string
type ItemId = string
type ItemIndex<T> = (item: T) => IndexKey

type ItemMap = Map<IndexKey, Set<ItemId>>

const getIntersection = <T>(set1: Set<T>, set2: Set<T>) => {
  const result = new Set<T>()
  const [shorter, longer] = set1.size >= set2.size ? [set2, set1] : [set1, set2]
  for (const i of shorter) {
    if (longer.has(i)) {
      result.add(i)
    }
  }
  return result
}

const notUndefined = <T>(obj: T | undefined): obj is T => obj !== undefined

export class ItemDatabase<T, I extends Record<string, ItemIndex<T>>> {
  primaryKey: ItemIndex<T> = () => ''
  itemMap = new Map<ItemId, T>()
  indexMap = {} as {
    [K in keyof I]: ItemMap
  }
  constructor(
    public items: T[],
    primaryKey: ItemIndex<T>,
    public indicies: I
  ) {
    this.primaryKey = primaryKey
    this.itemMap = items.reduce(
      (result, item) => (result.has(primaryKey(item)) ? result : result.set(primaryKey(item), item)),
      new Map<string, T>()
    )

    this.indexMap = items.reduce(
      (result, item) => {
        const id = primaryKey(item)
        Object.entries(indicies).forEach(([k, val]) => {
          const key = k as keyof typeof indicies
          const groupedKey = val(item)
          if (!result[key]) {
            result[key] = new Map<IndexKey, Set<ItemId>>()
          }

          let set = result[key].get(groupedKey)
          if (!set) {
            set = new Set()
            result[key].set(groupedKey, set)
          }
          set.add(id)
        })
        return result
      },
      {} as {
        [K in keyof I]: ItemMap
      }
    )
  }

  search = (...indicies: Array<{ index: keyof I; value: IndexKey }>) => {
    let skip = false
    let finalSet: Set<ItemId> | undefined = undefined
    for (const { index, value } of indicies) {
      const currentSet = this.indexMap[index]?.get(value)
      if (!currentSet) {
        skip = true
        break
      }
      finalSet = !finalSet ? currentSet : getIntersection(finalSet, currentSet)
    }
    if (skip || !finalSet) return []
    return Array.from(finalSet)
      .map((id) => this.itemMap.get(id))
      .filter(notUndefined)
  }
}
